"use client";

import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { CardListPagination } from "@widgets/card-list-pagination";
import { Section } from "@widgets/section";

import type { FilterItemType } from "@shared/content-types/types";

import type { PaginatedResponse } from "../../hooks/use-filter-pagination";
import { useFilterPagination } from "../../hooks/use-filter-pagination";
import { Container } from "../container";
import type { MultiBadgeItem } from "../filters-with-multi-badges";
import { FiltersWithMultiBadges } from "../filters-with-multi-badges";
import { Icon } from "../icon";
import { StyledButton } from "../styled-button";

import s from "./filter-pagination-container-with-multi-badges.module.scss";

export interface FilterPaginationContainerWithMultiBadgesProps<T> {
  initialContent: PaginatedResponse<T>;
  filters: FilterItemType[];
  badges: MultiBadgeItem[];
  badgesFilterKey?: string;
  fetchFunction: (
    filter: string,
    page: number,
  ) => Promise<PaginatedResponse<T>>;
  renderCard: (item: T, index: number) => ReactNode;
  className?: string;
  sectionClassName?: string;
  filtersClassName?: string;
  paginationClassName?: string;
  cardsClassName?: string;
  paginationThreshold?: number;
  filterProps?: Partial<ComponentProps<typeof FiltersWithMultiBadges>>;
  mapTo: string;
}

const buildCombinedFilter = (
  baseFilter: string,
  selectedBadges: string[],
  allBadgeUids: string[],
  badgesFilterKey: string,
) => {
  // Нет бейджей или выбор пустой — не фильтруем.
  if (allBadgeUids.length === 0 || selectedBadges.length === 0) {
    return baseFilter;
  }

  const isAllSelected = selectedBadges.length === allBadgeUids.length;

  // "Все" (без фильтра по бейджам) — не добавляем параметр в запрос.
  if (isAllSelected) {
    return baseFilter;
  }

  const value = selectedBadges.join(",");
  const key = `${badgesFilterKey}=`;

  if (baseFilter === "all") {
    return `${key}${value}`;
  }

  return `${baseFilter}&${key}${value}`;
};

const stripBadgesFromFilter = (filter: string, badgesFilterKey: string) => {
  if (!filter) return "all";

  const excludedKey = `${badgesFilterKey}_exclude`;
  // TODO: excludedKey оставлен для обратной совместимости со старым форматом.

  const parts = filter
    .split("&")
    .map((p) => p.trim())
    .filter(Boolean)
    .filter(
      (p) =>
        !p.startsWith(`${badgesFilterKey}=`) &&
        !p.startsWith(`${excludedKey}=`),
    );

  return parts.length > 0 ? parts.join("&") : "all";
};

export const FilterPaginationContainerWithMultiBadges = <T,>(
  props: FilterPaginationContainerWithMultiBadgesProps<T>,
) => {
  const {
    initialContent,
    filters,
    badges,
    badgesFilterKey = "badges",
    fetchFunction,
    renderCard,
    className,
    sectionClassName,
    filtersClassName,
    paginationClassName,
    cardsClassName,
    paginationThreshold = 6,
    filterProps,
    mapTo,
  } = props;

  const allBadgeUids = useMemo(
    () =>
      Array.isArray(badges)
        ? badges.map((b) => b?.uid).filter((uid): uid is string => Boolean(uid))
        : [],
    [badges],
  );

  const [selectedBadges, setSelectedBadges] = useState<string[]>([]);

  const {
    currentContent,
    activeFilter,
    handleChangeFilter: handleChangeBaseFilter,
    handleClickOnNext,
    handleClickOnPrev,
    handleClickOnPagination,
  } = useFilterPagination({
    initialContent,
    fetchFunction,
  });

  const filtersWithValues = useMemo(() => {
    if (!mapTo) {
      return filters;
    }

    const relationKey = `${mapTo.replace(/-/g, "_")}_points`;

    return filters.filter((item) => {
      const value = (item as Record<string, unknown>)[relationKey];
      return Array.isArray(value) && value.length > 0;
    });
  }, [mapTo, filters]);

  const handleChangeFilter = useCallback(
    (nextBaseFilter: string) => {
      const base = stripBadgesFromFilter(nextBaseFilter, badgesFilterKey);

      const combined = buildCombinedFilter(
        base,
        selectedBadges,
        allBadgeUids,
        badgesFilterKey,
      );

      handleChangeBaseFilter(combined);
    },
    [allBadgeUids, badgesFilterKey, handleChangeBaseFilter, selectedBadges],
  );

  const handleSelectedBadgesChange = useCallback(
    (next: string[]) => {
      setSelectedBadges(next);

      const base = stripBadgesFromFilter(activeFilter, badgesFilterKey);

      const combined = buildCombinedFilter(
        base,
        next,
        allBadgeUids,
        badgesFilterKey,
      );

      handleChangeBaseFilter(combined);
    },
    [activeFilter, allBadgeUids, badgesFilterKey, handleChangeBaseFilter],
  );

  const currentFilterPoints: PaginatedResponse<T> = useMemo(() => {
    if (!mapTo) {
      return initialContent;
    }

    const relationKey = `${mapTo.replace(/-/g, "_")}_points`;
    const filterName = activeFilter.replace(/&.*/, "");
    const isAll =
      filterName === "all" || filterName.replace(/=.*/, "") === badgesFilterKey;

    if (isAll) {
      return initialContent;
    }

    const currentFilter = filtersWithValues.find(
      (item) => item.uid === filterName,
    );

    if (currentFilter) {
      return {
        data: (currentFilter as Record<string, unknown>)[relationKey],
      } as PaginatedResponse<T>;
    }

    return initialContent;
  }, [activeFilter]);

  const currentBadges = useMemo(() => {
    const badgeList = badges ?? [];
    if (!mapTo) {
      return badgeList
        .map((b) => b?.uid)
        .filter((uid): uid is string => Boolean(uid));
    }

    if ((currentFilterPoints?.data?.length ?? 0) === 0) {
      return [];
    }

    const relationKey = `${mapTo.replace(/-/g, "_")}_badge_types`;

    const badgeUidsInCurrentData = new Set<string>();

    currentFilterPoints.data.forEach((item) => {
      const relatedBadges = (item as unknown as Record<string, unknown>)[
        relationKey
      ] as Array<{ uid?: string }> | undefined;

      if (Array.isArray(relatedBadges)) {
        relatedBadges.forEach((badge) => {
          if (badge?.uid) {
            badgeUidsInCurrentData.add(badge.uid);
          }
        });
      }
    });

    const currentSelected = selectedBadges.filter((uid) =>
      badgeUidsInCurrentData.has(uid),
    );

    if (currentSelected.length !== selectedBadges.length) {
      setSelectedBadges(currentSelected);
    }

    if (badgeUidsInCurrentData.size === 0) {
      return badgeList
        .map((b) => b?.uid)
        .filter((uid): uid is string => Boolean(uid));
    }

    return badgeList
      .filter(
        (badge) => badge?.uid != null && badgeUidsInCurrentData.has(badge.uid),
      )
      .map((badge) => badge.uid as string);
  }, [currentFilterPoints, mapTo, selectedBadges, badges]);

  return (
    <Section className={clsx(s.root, sectionClassName)}>
      <Container className={className}>
        {filtersWithValues.length > 0 && (
          <FiltersWithMultiBadges
            filters={filtersWithValues}
            className={clsx(s.filters, filtersClassName)}
            onChangeFilter={handleChangeFilter}
            badges={badges}
            currentBadges={currentBadges}
            selectedBadges={selectedBadges}
            onSelectedBadgesChange={handleSelectedBadgesChange}
            {...filterProps}
          >
            <StyledButton
              size="s"
              href={`/map?type=resort&filters=%5B"${mapTo}"%5D`}
              aria-label="Показать на карте курорта"
            >
              <Icon name="map" size={24} />
            </StyledButton>
          </FiltersWithMultiBadges>
        )}

        {currentContent?.data?.length > 0 && (
          <>
            <div className={cardsClassName}>
              {currentContent.data.map((item, index) =>
                item != null ? renderCard(item, index) : null,
              )}
            </div>
            {(currentContent?.meta?.pagination?.total ?? 0) >
              paginationThreshold && (
              <CardListPagination
                className={clsx(s.pagination, paginationClassName)}
                totalPages={currentContent?.meta?.pagination?.pageCount ?? 1}
                currentPageIndex={currentContent?.meta?.pagination?.page ?? 1}
                onNextClick={handleClickOnNext}
                onPrevClick={handleClickOnPrev}
                onPaginationClick={handleClickOnPagination}
              />
            )}
          </>
        )}
      </Container>
    </Section>
  );
};

FilterPaginationContainerWithMultiBadges.displayName =
  "FilterPaginationContainerWithMultiBadges";
